import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./AdditionalDetails.css"; // Reusing the same CSS
import {
  EntityType,
  ContractorService,
  ContractorInsuranceDetailsVM,
  ContractorLicenseDetailsVM,
  ContractorTaxDetailsVM,
  DocumentService,
} from "@propertelligent/client-api"; // Assuming these are in the client-api
import EmptyState from "../emptystate";
import additionalDetailsIcon from "../../assets/additional-details.svg";
import ActionMenu from "../actionMenus/action-menu";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import SimpleSpinner from "../../components/loader";
import SimpleButton from "../buttons/Simple-button";
import apiHelper from "../../utils/apiHelper";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import ManageContractorAdditionalDetailsForm from "./ManageContractorAdditionalDetailsForm";
import Card from "../card";

// Define props for the component
interface ContractorAdditionalDetailsProps {
  entityId: number;
  entityTypeId: number;
}

// Map for displaying contractor business type
const contractorBusinessTypeMap = {
  1: "Individual",
  2: "Business",
};

const ContractorAdditionalDetails: React.FC<ContractorAdditionalDetailsProps> = ({ entityId, entityTypeId }) => {
  // State for each detail type
  const [insuranceDetails, setInsuranceDetails] = useState<ContractorInsuranceDetailsVM | null>(null);
  const [licenseDetails, setLicenseDetails] = useState<ContractorLicenseDetailsVM | null>(null);
  const [taxDetails, setTaxDetails] = useState<ContractorTaxDetailsVM | null>(null);

  // UI State
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<{
    defaultValues: any | null;
    defaultDetailType: string | null;
  }>({ defaultValues: null, defaultDetailType: null });
  const [showConfirmation, setShowConfirmation] = useState<{ type: string | null; show: boolean }>({ type: null, show: false });
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if the entity is a contractor
  const isContractor = entityTypeId === EntityType.Contractor;

  // Function to fetch all contractor details
  const fetchContractorDetails = useCallback(async () => {
    if (!isContractor) return;
    setLoading(true);

    try {
      const [insuranceRes, licenseRes, taxRes] = await Promise.allSettled([
        ContractorService.getApiContractorInsuranceDetails(entityId),
        ContractorService.getApiContractorLicenseDetails(entityId),
        ContractorService.getApiContractorTaxDetails(entityId),
      ]);

      setInsuranceDetails(insuranceRes.status === 'fulfilled' ? insuranceRes.value.success : null);
      setLicenseDetails(licenseRes.status === 'fulfilled' ? licenseRes.value.success : null);
      setTaxDetails(taxRes.status === 'fulfilled' ? taxRes.value.success : null);

    } catch (err) {
      console.error("Error fetching contractor details:", err);
    } finally {
      setLoading(false);
    }
  }, [entityId, isContractor]);

  useEffect(() => {
    if (entityId) fetchContractorDetails();
  }, [entityId, fetchContractorDetails]);

  // Handle delete action
  const handleDelete = async (type: string) => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      let deletePromise;
      switch (type) {
        case "insurance":
          deletePromise = ContractorService.deleteApiContractorInsuranceDetails(entityId);
          break;
        case "license":
          deletePromise = ContractorService.deleteApiContractorLicenseDetails(entityId);
          break;
        case "tax":
          deletePromise = ContractorService.deleteApiContractorTaxDetails(entityId);
          break;
        default:
          return;
      }
      await apiHelper(() => deletePromise, {
        successMessage: `${type.charAt(0).toUpperCase() + type.slice(1)} details deleted successfully`,
        showNotification: true,
      });
      fetchContractorDetails(); // Refresh data after deletion
    } catch (error) {
      console.error(`Error deleting ${type} details:`, error);
    } finally {
      setIsDeleting(false);
      setShowConfirmation({ type: null, show: false });
    }
  };

  // ContractorAdditionalDetails.tsx component ke andar, state declarations ke baad yeh function add karein

  const handleDownloadDocument = async (documentId: number, fileName: string = "document.pdf") => {
    if (!documentId) return;

    try {
      // Assuming DocumentService is available and imported
      const response = await DocumentService.getApiDocumentBase641(documentId);

      if (response?.success) {
        // Create a link element
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${response.success}`;
        link.download = fileName;

        // Append to the document, click it, and then remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Failed to get base64 data for the document.");
        // Optionally, show a notification to the user
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  // Generate menu items for ActionMenu
  const menuItems = (type: "insurance" | "license" | "tax", details: any) => [
    {
      icon: editIcon,
      label: details ? "Edit" : "Add",
      action: () => {
        setFormData({ defaultValues: details, defaultDetailType: type });
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false,
    },
    ...(details ? [{ isDivider: true }] : []),
    ...(details ? [{
      icon: deleteIcon,
      label: "Delete",
      action: () => setShowConfirmation({ type, show: true }),
      isDivider: false,
      isDestructive: true,
    }] : []),
  ];

  // Determine if the main "Add Details" button should be visible
  const isAddButtonVisible = !insuranceDetails || !licenseDetails || !taxDetails;

  if (loading) {
    return (
     <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  return (
    <>
      <div className="top-listing-header-top" style={{ paddingTop: "24px" }}>
        <h2 className="H2">Additional Details</h2>
        {isAddButtonVisible && isContractor && (
          <SimpleButton
            label={"Add Details"}
            onClick={() => {
              // Determine which form to show by default
              const defaultType = !insuranceDetails ? "insurance" : !licenseDetails ? "license" : "tax";
              setFormData({ defaultValues: null, defaultDetailType: defaultType });
              setModalOpen(true);
            }}
          />
        )}
      </div>

      <Card className="additional-details-container" isbordered={false} backgroundColor="transparent" >
        

        {!loading && isContractor && (
          <div className="row">
            {/* Insurance Details Section */}
            <div className="col-12">
              <div className="table-responsive additional-details-table">
                <div className="table-header">
                  <h4 className="mb-0">Insurance Details</h4>
                  <ActionMenu menuItems={menuItems("insurance", insuranceDetails)} trigger={<button className="action-button iconStyleWithWhiteFilter"><img src={dropdownIcon} alt="More" /></button>} />
                </div>
                {/* In ContractorAdditionalDetails.tsx, replace the insurance details table */}

                {insuranceDetails ? (
                  <table className="table">
                    <tbody>
                      <tr><td>Policy Number</td><td>{insuranceDetails.policyNumber || "-"}</td></tr>
                      <tr><td>Valid Until</td><td>{insuranceDetails?.formatted?.ValidUntilDateOnly || "-"}</td></tr>
                      <tr><td>Contact Name</td><td>{insuranceDetails.contactInfo?.fullName || "-"}</td></tr>
                      <tr><td>Contact Email</td><td>{insuranceDetails.contactInfo?.email || "-"}</td></tr>
                      <tr><td>Contact Phone</td><td>{insuranceDetails.contactInfo?.formatted?.PhoneNumber || "-"}</td></tr>

                      {/* --- ADD THIS NEW ROW --- */}
                      {!!insuranceDetails.insuranceDocumentId && (
                        <tr>
                          <td>Document</td>
                          <td>
                            <button
                              className="btn btn-link p-0"
                              onClick={() => handleDownloadDocument(insuranceDetails.insuranceDocumentId!, 'Insurance.pdf')}>
                              Download PDF
                            </button>
                          </td>
                        </tr>
                      )}
                      {/* --- END OF NEW ROW --- */}

                    </tbody>
                  </table>
                ) : <EmptyState icon={additionalDetailsIcon} heading="Insurance Details Not Added" isButton={false} isBorder={false} />}
              </div>
            </div>

            {/* License Details Section */}
            <div className="col-12">
              <div className="table-responsive additional-details-table">
                <div className="table-header">
                  <h4 className="mb-0">License Details</h4>
                  <ActionMenu menuItems={menuItems("license", licenseDetails)} trigger={<button className="action-button iconStyleWithWhiteFilter"><img src={dropdownIcon} alt="More" /></button>} />
                </div>
                {/* In ContractorAdditionalDetails.tsx, replace the license details table */}

                {licenseDetails ? (
                  <table className="table">
                    <tbody>
                      <tr><td>License Number</td><td>{licenseDetails.licenseNumber || "-"}</td></tr>
                      <tr><td>Valid Until</td><td>{licenseDetails?.formatted?.ValidUntilDateOnly || "-"}</td></tr>

                      {/* --- ADD THIS NEW ROW --- */}
                      {!!licenseDetails.licenseDocumentId && (
                        <tr>
                          <td>Document</td>
                          <td>
                            <button
                              className="btn btn-link p-0"
                              onClick={() => handleDownloadDocument(licenseDetails.licenseDocumentId!, 'License.pdf')}>
                              Download PDF
                            </button>
                          </td>
                        </tr>
                      )}
                      {/* --- END OF NEW ROW --- */}

                    </tbody>
                  </table>
                ) : <EmptyState icon={additionalDetailsIcon} heading="License Details Not Added" isButton={false} isBorder={false} />}
              </div>
            </div>

            {/* Tax Details Section */}
            <div className="col-12">
              <div className="table-responsive additional-details-table">
                <div className="table-header">
                  <h4 className="mb-0">Tax Details</h4>
                  <ActionMenu menuItems={menuItems("tax", taxDetails)} trigger={<button className="action-button iconStyleWithWhiteFilter"><img src={dropdownIcon} alt="More" /></button>} />
                </div>
                {taxDetails ? (
                  <table className="table">
                    <tbody>
                      <tr><td>Business Type</td><td>{taxDetails?.formatted?.ContractorBusinessTypesId || "-"}</td></tr>
                      {taxDetails.contractorBusinessTypesId === 2 && <tr><td>Company Name</td><td>{taxDetails.companyName || "-"}</td></tr>}
                      <tr><td>Tax ID</td><td>{taxDetails.taxId || "-"}</td></tr>
                      <tr><td>Address</td><td>{taxDetails.address ? `${taxDetails.address.streetAddress}, ${taxDetails.address.city}, ${taxDetails.address.state} ${taxDetails.address.zip}` : "-"}</td></tr>
                      <tr><td>Suite No</td><td>{taxDetails.suiteNo || "-"}</td></tr>
                    </tbody>
                  </table>
                ) : <EmptyState icon={additionalDetailsIcon} heading="Tax Details Not Added" isButton={false} isBorder={false} />}
              </div>
            </div>
          </div>
        )}
      </Card>

      <ManageContractorAdditionalDetailsForm
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setFormData({ defaultValues: null, defaultDetailType: null });
        }}
        entityId={entityId}
        defaultValues={formData.defaultValues}
        defaultDetailType={formData.defaultDetailType}
        refreshData={fetchContractorDetails}
        hasInsuranceDetails={!!insuranceDetails}
        hasLicenseDetails={!!licenseDetails}
        hasTaxDetails={!!taxDetails}
      />

      {showConfirmation.show && (
  <ConfirmationPopup
    isOpen={true}
    title={`Delete ${showConfirmation.type?.charAt(0).toUpperCase() + showConfirmation.type?.slice(1)} Details`}
    message={`Are you sure you want to delete the ${showConfirmation.type} details? This action cannot be undone.`}
    primaryButtonLabel="Delete"
    secondaryButtonLabel="Cancel"
    onPrimaryAction={() => handleDelete(showConfirmation.type!)}
    onSecondaryAction={() => setShowConfirmation({ type: null, show: false })}
    onClose={() => setShowConfirmation({ type: null, show: false })}
    isPrimaryDestructive={true}
    isLoading={isDeleting}
    loadingLabel="Deleting..."
  />
)}
    </>
  );
};

export default ContractorAdditionalDetails;